import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

interface Inquiry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  title: string;
  content: string;
  status: string;
  reply: string | null;
  created_at: string;
}

export default function InquiryPage() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    content: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInquiries(data || []);
    } catch (error) {
      console.error('문의 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('inquiries')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          title: formData.title,
          content: formData.content,
          status: 'pending'
        }]);

      if (error) throw error;

      alert('문의가 성공적으로 등록되었습니다!');
      setFormData({ name: '', email: '', phone: '', title: '', content: '' });
      setShowForm(false);
      fetchInquiries();
    } catch (error) {
      console.error('문의 등록 실패:', error);
      alert('문의 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { text: '답변대기', color: 'bg-amber-100 text-amber-700' },
      answered: { text: '답변완료', color: 'bg-emerald-100 text-emerald-700' }
    };
    const badge = badges[status as keyof typeof badges] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 헤더 */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">문의 게시판</h1>
            <p className="text-lg text-gray-600">궁금하신 사항을 자유롭게 문의해주세요</p>
          </div>

          {/* 문의 작성 버튼 */}
          <div className="mb-8 flex justify-end">
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line text-xl"></i>
              문의하기
            </button>
          </div>

          {/* 문의 작성 폼 */}
          {showForm && (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">새 문의 작성</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="이름을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이메일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="이메일을 입력하세요"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    연락처
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="연락처를 입력하세요 (선택사항)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="문의 제목을 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    내용 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="문의 내용을 입력하세요"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {submitting ? '등록 중...' : '문의 등록'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 문의 목록 */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">문의 목록을 불러오는 중...</p>
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
              <i className="ri-question-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600">아직 등록된 문의가 없습니다</p>
              <p className="text-gray-500 mt-2">첫 번째 문의를 남겨보세요!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 overflow-hidden"
                >
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => setSelectedInquiry(selectedInquiry?.id === inquiry.id ? null : inquiry)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          {getStatusBadge(inquiry.status)}
                          <span className="text-sm text-gray-500">{formatDate(inquiry.created_at)}</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{inquiry.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {inquiry.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-mail-line"></i>
                            {inquiry.email}
                          </span>
                        </div>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600 transition-colors">
                        <i className={`ri-arrow-${selectedInquiry?.id === inquiry.id ? 'up' : 'down'}-s-line text-2xl`}></i>
                      </button>
                    </div>
                  </div>

                  {/* 상세 내용 */}
                  {selectedInquiry?.id === inquiry.id && (
                    <div className="border-t border-gray-100 bg-gray-50 p-6">
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">문의 내용</h4>
                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{inquiry.content}</p>
                      </div>

                      {inquiry.reply && (
                        <div className="bg-teal-50 rounded-lg p-4 border-l-4 border-teal-600">
                          <h4 className="text-sm font-semibold text-teal-900 mb-2 flex items-center gap-2">
                            <i className="ri-reply-line"></i>
                            답변
                          </h4>
                          <p className="text-teal-800 whitespace-pre-wrap leading-relaxed">{inquiry.reply}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
